import { useState, useEffect } from 'react'
import { useAtom } from 'jotai'
import {
  Box,
  Typography,
  Card,
  CardContent,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  FormGroup,
  Checkbox,
  Menu,
  MenuItem,
  Alert,
  CircularProgress,
  Chip,
  Select,
  InputLabel,
  FormControl,
  FormHelperText,
  List,
  ListItem,
  ListItemText,
  DialogContentText,
} from '@mui/material'
import {
  Add as AddIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  CheckCircle as RequiredIcon,
  DragIndicator as DragIcon,
  Lock as LockIcon,
} from '@mui/icons-material'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { ApiError, stageGatesApi, workflowStagesApi, salesStagesApi, interviewStagesApi, jobApplicationStagesApi, StageGate, StageGateTask, TeamRole, WorkflowStageDefinition, SalesStageDefinition, InterviewStageDefinition, JobApplicationStageDefinition } from '../services/api'
import { useNotification } from '../hooks/useNotification'
import {
  GATEABLE_STAGE_VISIBILITY_ENTRIES,
  RECORD_SECTION_LABELS,
} from '../constants/stageVisibility'
import { STAGE_ENTRY_EMAIL_OPTIONS } from '../constants/stageEntryEmails'
import { userAtom } from '../state/auth'
import { workflowStageDefinitionsAtom } from '../state/clients'
import { salesStageDefinitionsAtom } from '../state/leads'
import PageHelpButton from '../components/PageHelpButton'
import { interviewStageDefinitionsAtom } from '../state/interviews'
import { jobApplicationStageDefinitionsAtom } from '../state/jobOpportunities'
import { isAdminOrManager } from '../utils/roles'

const WORKFLOW_STAGES = [
  'onboarding',
  'pre_north_star',
  'north_star',
  'strategy_session',
  'deliverables_in_progress',
  'deliverables_complete',
  'linkedin_workshop',
  'market_ready',
  'complete',
] as const

const FALLBACK_STAGE_LABELS: Record<string, string> = {
  onboarding: 'Onboarding',
  pre_north_star: 'Initial Consultation',
  north_star: 'North Star',
  strategy_session: 'Strategy Session',
  deliverables_in_progress: 'Deliverables in Progress',
  deliverables_complete: 'Deliverables Complete',
  linkedin_workshop: 'LinkedIn Workshop',
  market_ready: 'Market Ready',
  complete: 'Complete',
}

const ROLE_OPTIONS: { value: TeamRole; label: string }[] = [
  { value: 'sales', label: 'Sales' },
  { value: 'clientSuccess', label: 'Client Success' },
  { value: 'resumeWriter', label: 'Resume Writer' },
  { value: 'coach', label: 'Coach' },
]

const ROLE_LABELS: Record<TeamRole, string> = {
  sales: 'Sales',
  clientSuccess: 'Client Success',
  resumeWriter: 'Resume Writer',
  coach: 'Coach',
}

const STAGE_DEFAULT_ROLE: Record<string, TeamRole | null> = {
  onboarding: 'clientSuccess',
  pre_north_star: 'coach',
  north_star: 'resumeWriter',
  strategy_session: 'coach',
  deliverables_in_progress: 'resumeWriter',
  deliverables_complete: 'resumeWriter',
  linkedin_workshop: 'coach',
  market_ready: null,
  complete: null,
}

interface SortableStageItemProps {
  stageId: string
  label: string
  color: string
  // Job application stages don't carry teamRole — accept undefined as well as
  // null so the same item component renders for all four pipeline types.
  teamRole?: string | null
  isSystem: boolean
  onEdit: (stageId: string) => void
  onDelete: (stageId: string) => void
}

function SortableStageItem({ stageId, label, color, teamRole, isSystem, onEdit, onDelete }: SortableStageItemProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: stageId })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  return (
    <ListItem
      ref={setNodeRef}
      style={style}
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        mb: 1,
        backgroundColor: 'background.paper',
        '&:hover': { backgroundColor: 'action.hover' },
      }}
      secondaryAction={
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {isSystem && (
            <Chip
              icon={<LockIcon sx={{ fontSize: 14 }} />}
              label="Protected"
              size="small"
              variant="outlined"
              title="Delete is blocked while this stage is protected. Edit the stage to toggle protection off."
            />
          )}
          <IconButton size="small" onClick={() => onEdit(stageId)}>
            <EditIcon fontSize="small" />
          </IconButton>
          {!isSystem && (
            <IconButton size="small" onClick={() => onDelete(stageId)} sx={{ color: 'error.main' }}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      }
    >
      <Box {...attributes} {...listeners} sx={{ display: 'flex', alignItems: 'center', mr: 2, cursor: 'grab' }}>
        <DragIcon sx={{ color: 'text.secondary' }} />
      </Box>
      <Box
        sx={{
          width: 16,
          height: 16,
          borderRadius: '50%',
          backgroundColor: color,
          mr: 2,
          flexShrink: 0,
        }}
      />
      <ListItemText
        primary={label}
        // ListItemText's secondary slot renders as <p> by default, but we
        // put a Box + Chip (both <div>) inside, which is invalid HTML and
        // triggers a React hydration warning. Render secondary as a <div>.
        slotProps={{ secondary: { component: 'div' } }}
        secondary={
          <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
            <Typography variant="caption" color="text.secondary">
              ID: {stageId}
            </Typography>
            {teamRole && (
              <Chip
                label={ROLE_LABELS[teamRole as TeamRole]}
                size="small"
                sx={{ height: 18, fontSize: '0.65rem' }}
              />
            )}
          </Box>
        }
      />
    </ListItem>
  )
}

type PipelineType = 'client' | 'sales' | 'interview' | 'jobApplication'

export default function StageGates() {
  const [user] = useAtom(userAtom)
  const { showSuccess, showError } = useNotification()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [stageGates, setStageGates] = useState<StageGate[]>([])
  const [activeTab, setActiveTab] = useState(0)
  const [error, setError] = useState<string | null>(null)
  const [pipelineType, setPipelineType] = useState<PipelineType>('client')

  // Stage definitions for all four pipelines
  const [workflowStages, setWorkflowStages] = useAtom(workflowStageDefinitionsAtom)
  const [salesStages, setSalesStages] = useAtom(salesStageDefinitionsAtom)
  const [interviewStages, setInterviewStages] = useAtom(interviewStageDefinitionsAtom)
  const [jobApplicationStages, setJobApplicationStages] = useAtom(jobApplicationStageDefinitionsAtom)
  const [stagesLoading, setStagesLoading] = useState(false)

  // Stage management dialog states
  const [stageDialogOpen, setStageDialogOpen] = useState(false)
  const [editingStage, setEditingStage] = useState<string | null>(null) // stageId
  const [stageForm, setStageForm] = useState({
    label: '',
    color: '#66bb6a',
    teamRole: '' as TeamRole | '',
    hiddenPanels: [] as string[],
    entryEmail: '', // Client pipeline only: the email sent when a client enters the stage
    showValue: true, // Sales-specific field
    isProtected: false,
  })
  const [deleteStageDialogOpen, setDeleteStageDialogOpen] = useState(false)
  const [deletingStage, setDeletingStage] = useState<string | null>(null)

  // Dialog states
  const [taskDialogOpen, setTaskDialogOpen] = useState(false)
  const [editingTask, setEditingTask] = useState<StageGateTask | null>(null)
  const [taskForm, setTaskForm] = useState({
    title: '',
    description: '',
    required: true,
    ownerRole: '' as TeamRole | '',
  })

  // Menu state
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null)
  const [menuTask, setMenuTask] = useState<StageGateTask | null>(null)

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  const isAdmin = isAdminOrManager(user)
  // isManager merged into isAdmin via isAdminOrManager helper
  const canManage = isAdmin

  // Get active stages based on selected pipeline type
  const getCurrentStages = () => {
    switch (pipelineType) {
      case 'client':
        return workflowStages.filter((s) => s.isActive).sort((a, b) => Number(a.sortOrder) - Number(b.sortOrder))
      case 'sales':
        return salesStages.filter((s) => s.isActive).sort((a, b) => Number(a.sortOrder) - Number(b.sortOrder))
      case 'interview':
        return interviewStages.filter((s) => s.isActive).sort((a, b) => Number(a.sortOrder) - Number(b.sortOrder))
      case 'jobApplication':
        return jobApplicationStages.filter((s) => s.isActive).sort((a, b) => Number(a.sortOrder) - Number(b.sortOrder))
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const getCurrentStagesApi = (): any => {
    switch (pipelineType) {
      case 'client':
        return workflowStagesApi
      case 'sales':
        return salesStagesApi
      case 'interview':
        return interviewStagesApi
      case 'jobApplication':
        return jobApplicationStagesApi
    }
  }

  const activeStages = getCurrentStages()
  const currentStageId = activeStages[activeTab]?.stageId || WORKFLOW_STAGES[activeTab]
  const currentStageGate = stageGates.find((g) => g.stageId === currentStageId) || {
    stageId: currentStageId,
    tasks: [],
    allowOverride: true,
  }

  useEffect(() => {
    loadStageGates()
    loadAllStages()
  }, [])

  const loadAllStages = async () => {
    await Promise.all([
      loadWorkflowStages(),
      loadSalesStages(),
      loadInterviewStages(),
      loadJobApplicationStages(),
    ])
  }

  const loadStageGates = async () => {
    try {
      setLoading(true)
      setError(null)
      const gates = await stageGatesApi.list()
      setStageGates(gates)
    } catch (err) {
      console.error('Failed to load stage gates:', err)
      setError('Failed to load stage gate configurations')
    } finally {
      setLoading(false)
    }
  }

  const loadWorkflowStages = async (forceRefresh = false) => {
    try {
      setStagesLoading(true)
      const stages = await workflowStagesApi.list(forceRefresh)
      setWorkflowStages(stages)
    } catch (err) {
      console.error('Failed to load workflow stages:', err)
      showError('Failed to load workflow stages')
    } finally {
      setStagesLoading(false)
    }
  }

  const loadSalesStages = async (forceRefresh = false) => {
    try {
      setStagesLoading(true)
      const stages = await salesStagesApi.list(forceRefresh)
      setSalesStages(stages)
    } catch (err) {
      console.error('Failed to load sales stages:', err)
      showError('Failed to load sales stages')
    } finally {
      setStagesLoading(false)
    }
  }

  const loadInterviewStages = async (forceRefresh = false) => {
    try {
      setStagesLoading(true)
      const stages = await interviewStagesApi.list(forceRefresh)
      setInterviewStages(stages)
    } catch (err) {
      console.error('Failed to load interview stages:', err)
      showError('Failed to load interview stages')
    } finally {
      setStagesLoading(false)
    }
  }

  const loadJobApplicationStages = async (forceRefresh = false) => {
    try {
      setStagesLoading(true)
      const stages = await jobApplicationStagesApi.list(forceRefresh)
      setJobApplicationStages(stages)
    } catch (err) {
      console.error('Failed to load job application stages:', err)
      showError('Failed to load job application stages')
    } finally {
      setStagesLoading(false)
    }
  }

  const loadCurrentStages = async (forceRefresh = false) => {
    switch (pipelineType) {
      case 'client':
        return loadWorkflowStages(forceRefresh)
      case 'sales':
        return loadSalesStages(forceRefresh)
      case 'interview':
        return loadInterviewStages(forceRefresh)
      case 'jobApplication':
        return loadJobApplicationStages(forceRefresh)
    }
  }

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) return

    const currentStages = getCurrentStages()
    const oldIndex = currentStages.findIndex((s) => s.stageId === active.id)
    const newIndex = currentStages.findIndex((s) => s.stageId === over.id)

    if (oldIndex === -1 || newIndex === -1) return

    const reordered = arrayMove(currentStages, oldIndex, newIndex)

    // Update sortOrder property for optimistic UI update
    const reorderedWithSortOrder = reordered.map((stage, index) => ({
      ...stage,
      sortOrder: String(index) as string | number,
    }))

    // Update the appropriate state based on pipeline type
    switch (pipelineType) {
      case 'client':
        setWorkflowStages(reorderedWithSortOrder as WorkflowStageDefinition[])
        break
      case 'sales':
        setSalesStages(reorderedWithSortOrder as SalesStageDefinition[])
        break
      case 'interview':
        setInterviewStages(reorderedWithSortOrder as InterviewStageDefinition[])
        break
      case 'jobApplication':
        setJobApplicationStages(reorderedWithSortOrder as JobApplicationStageDefinition[])
        break
    }

    try {
      const stageIds = reorderedWithSortOrder.map((s) => s.stageId)
      const api = getCurrentStagesApi()
      await api.reorder(stageIds)
      showSuccess('Stage order updated')
    } catch (err) {
      console.error('Failed to reorder stages:', err)
      showError('Failed to reorder stages')
      // Revert on error
      await loadCurrentStages(true) // Force refresh on error
    }
  }

  const handleOpenStageDialog = (stageId?: string) => {
    if (stageId) {
      const currentStages = getCurrentStages()
      const stage = currentStages.find((s) => s.stageId === stageId)
      if (!stage) return
      setEditingStage(stageId)
      setStageForm({
        label: stage.label,
        color: stage.color,
        // Job application stages don't have teamRole; cast through unknown so
        // TS doesn't complain about the optional field across pipeline types.
        teamRole: ((stage as unknown as { teamRole?: TeamRole }).teamRole as TeamRole) || '',
        showValue: pipelineType === 'sales' ? (stage as unknown as { showValue?: boolean }).showValue ?? true : true,
        isProtected: !!stage.isSystem,
        hiddenPanels:
          pipelineType === 'client'
            ? workflowStages.find((s) => s.stageId === stageId)?.hiddenPanels ?? []
            : [],
        entryEmail:
          pipelineType === 'client'
            ? workflowStages.find((s) => s.stageId === stageId)?.entryEmail ?? ''
            : '',
      })
    } else {
      setEditingStage(null)
      setStageForm({
        label: '',
        color: '#66bb6a',
        teamRole: '',
        showValue: true,
        isProtected: false,
        hiddenPanels: [],
        entryEmail: '',
      })
    }
    setStageDialogOpen(true)
  }

  const handleCloseStageDialog = () => {
    setStageDialogOpen(false)
    setEditingStage(null)
    setStageForm({ label: '', color: '#66bb6a', teamRole: '', showValue: true, isProtected: false, hiddenPanels: [], entryEmail: '' })
  }

  const handleSaveStage = async () => {
    if (!stageForm.label.trim()) {
      showError('Stage label is required')
      return
    }

    try {
      setSaving(true)
      const api = getCurrentStagesApi()

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const stageData: any = {
        label: stageForm.label,
        color: stageForm.color,
      }

      // teamRole is meaningful for client/sales/interview pipelines but not
      // for job applications. Omit the field entirely on the wire when it
      // doesn't apply so the payload matches the lambda's expected shape.
      if (pipelineType !== 'jobApplication') {
        stageData.teamRole = stageForm.teamRole || null
      }

      // The entry email belongs to the client pipeline only. Sent on create as
      // well as update, so a new stage can be given its email straight away;
      // null is how "no email" is stored.
      if (pipelineType === 'client') {
        stageData.entryEmail = stageForm.entryEmail || null
      }

      // Add showValue only for sales pipeline
      if (pipelineType === 'sales') {
        stageData.showValue = stageForm.showValue
      }

      // Send isSystem only on update — create always starts unprotected and
      // the admin can toggle protection on afterward via Edit.
      if (editingStage) {
        stageData.isSystem = stageForm.isProtected
        // Only the client record has configurable panels. Sent on update
        // only, like isSystem — a new stage starts hiding nothing.
        if (pipelineType === 'client') {
          stageData.hiddenPanels = stageForm.hiddenPanels
        }
        await api.update(editingStage, stageData)
        showSuccess('Stage updated')
      } else {
        await api.create(stageData)
        showSuccess('Stage created')
      }

      await loadCurrentStages(true) // Force refresh after create/update
      handleCloseStageDialog()
    } catch (err: any) {
      console.error('Failed to save stage:', err)
      showError(err.message || 'Failed to save stage')
    } finally {
      setSaving(false)
    }
  }

  const handleOpenDeleteDialog = (stageId: string) => {
    setDeletingStage(stageId)
    setDeleteStageDialogOpen(true)
  }

  const handleCloseDeleteDialog = () => {
    setDeleteStageDialogOpen(false)
    setDeletingStage(null)
  }

  const handleConfirmDelete = async () => {
    if (!deletingStage) return

    try {
      setSaving(true)
      const api = getCurrentStagesApi()
      await api.delete(deletingStage)
      showSuccess('Stage deleted')
      await loadCurrentStages(true) // Force refresh after delete
      handleCloseDeleteDialog()
    } catch (err: unknown) {
      console.error('Failed to delete stage:', err)
      const errMessage = err instanceof Error ? err.message : String(err)
      let message: string
      if (errMessage.includes('stage_has_clients')) {
        // Naming the number is the difference between a refusal and an
        // instruction: it says how much work stands between here and deleting
        // the stage, which is what the person is about to go and do.
        const count = err instanceof ApiError ? Number(err.details?.clientCount) : NaN
        message = Number.isFinite(count)
          ? `Cannot delete — move ${count} client${count === 1 ? '' : 's'} out of this stage first`
          : 'Cannot delete — clients are currently in this stage'
      } else if (errMessage.includes('stage_has_applications')) {
        message = 'Cannot delete — job applications are currently in this stage'
      } else if (errMessage.includes('stage_has_interviews')) {
        message = 'Cannot delete — interviews are currently in this stage'
      } else if (errMessage.includes('stage_has_leads')) {
        message = 'Cannot delete — leads are currently in this stage'
      } else {
        message = errMessage || 'Failed to delete stage'
      }
      showError(message)
      handleCloseDeleteDialog()
    } finally {
      setSaving(false)
    }
  }

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue)
  }

  const handleOpenTaskDialog = (task?: StageGateTask) => {
    if (task) {
      setEditingTask(task)
      setTaskForm({
        title: task.title,
        description: task.description || '',
        required: task.required,
        ownerRole: task.ownerRole || STAGE_DEFAULT_ROLE[currentStageId] || '',
      })
    } else {
      setEditingTask(null)
      setTaskForm({
        title: '',
        description: '',
        required: true,
        ownerRole: STAGE_DEFAULT_ROLE[currentStageId] || '',
      })
    }
    setTaskDialogOpen(true)
    handleCloseMenu()
  }

  const handleCloseTaskDialog = () => {
    setTaskDialogOpen(false)
    setEditingTask(null)
    setTaskForm({ title: '', description: '', required: true, ownerRole: '' })
  }

  const handleSaveTask = async () => {
    if (!taskForm.title.trim()) {
      showError('Task title is required')
      return
    }

    try {
      setSaving(true)

      const updatedTasks = editingTask
        ? currentStageGate.tasks.map((t) =>
            t.id === editingTask.id
              ? { ...t, title: taskForm.title, description: taskForm.description || undefined, required: taskForm.required, ownerRole: taskForm.ownerRole || undefined }
              : t
          )
        : [
            ...currentStageGate.tasks,
            {
              id: '', // Will be assigned by backend
              title: taskForm.title,
              description: taskForm.description || undefined,
              required: taskForm.required,
              sortOrder: currentStageGate.tasks.length,
              ownerRole: taskForm.ownerRole || undefined,
            },
          ]

      const updatedGate = await stageGatesApi.update({
        stageId: currentStageId,
        tasks: updatedTasks,
        allowOverride: currentStageGate.allowOverride,
      })

      setStageGates((prev) => {
        const existing = prev.findIndex((g) => g.stageId === currentStageId)
        if (existing >= 0) {
          const updated = [...prev]
          updated[existing] = updatedGate
          return updated
        }
        return [...prev, updatedGate]
      })

      showSuccess(editingTask ? 'Task updated' : 'Task added')
      handleCloseTaskDialog()
    } catch (err) {
      console.error('Failed to save task:', err)
      showError('Failed to save task')
    } finally {
      setSaving(false)
    }
  }

  const handleDeleteTask = async () => {
    if (!menuTask) return

    try {
      setSaving(true)
      const updatedGate = await stageGatesApi.deleteTask(currentStageId, menuTask.id)

      setStageGates((prev) => {
        const existing = prev.findIndex((g) => g.stageId === currentStageId)
        if (existing >= 0) {
          const updated = [...prev]
          updated[existing] = updatedGate
          return updated
        }
        return prev
      })

      showSuccess('Task deleted')
      handleCloseMenu()
    } catch (err) {
      console.error('Failed to delete task:', err)
      showError('Failed to delete task')
    } finally {
      setSaving(false)
    }
  }

  const handleToggleOverride = async () => {
    try {
      setSaving(true)
      const updatedGate = await stageGatesApi.update({
        stageId: currentStageId,
        tasks: currentStageGate.tasks,
        allowOverride: !currentStageGate.allowOverride,
      })

      setStageGates((prev) => {
        const existing = prev.findIndex((g) => g.stageId === currentStageId)
        if (existing >= 0) {
          const updated = [...prev]
          updated[existing] = updatedGate
          return updated
        }
        return [...prev, updatedGate]
      })

      showSuccess(`Override ${updatedGate.allowOverride ? 'enabled' : 'disabled'}`)
    } catch (err) {
      console.error('Failed to update override setting:', err)
      showError('Failed to update override setting')
    } finally {
      setSaving(false)
    }
  }

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>, task: StageGateTask) => {
    setMenuAnchor(event.currentTarget)
    setMenuTask(task)
  }

  const handleCloseMenu = () => {
    setMenuAnchor(null)
    setMenuTask(null)
  }

  if (!canManage) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Typography variant="h5" color="text.secondary">
          You don&apos;t have permission to manage stage gates.
        </Typography>
      </Box>
    )
  }

  const getPipelineTitle = () => {
    switch (pipelineType) {
      case 'client':
        return 'Client Workflow Stages'
      case 'sales':
        return 'Sales Pipeline Stages'
      case 'interview':
        return 'Interview Prep Stages'
      case 'jobApplication':
        return 'Job Application Stages'
    }
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h4" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>Stage Management</Typography>
          <PageHelpButton articleId="admin-configure-stages" />
        </Box>
      </Box>

      {/* Pipeline Type Selector */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ p: 0 }}>
          <Tabs
            value={pipelineType}
            onChange={(_, newValue) => {
              setPipelineType(newValue as PipelineType)
              setActiveTab(0) // Reset to first tab when switching pipeline type
            }}
            sx={{ borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab value="client" label="Client Workflow" />
            <Tab value="sales" label="Sales Pipeline" />
            <Tab value="interview" label="Interview Prep" />
            <Tab value="jobApplication" label="Job Applications" />
          </Tabs>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Stage Management Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">{getPipelineTitle()}</Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <IconButton
                size="small"
                aria-label="Reload stages"
                onClick={() => loadCurrentStages(true)}
                disabled={stagesLoading}
              >
                <RefreshIcon />
              </IconButton>
              <Button
                variant="outlined"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => handleOpenStageDialog()}
                disabled={stagesLoading || saving}
              >
                Add Stage
              </Button>
            </Box>
          </Box>

          {stagesLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={24} sx={{ color: '#C41E3D' }} />
            </Box>
          ) : (
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={activeStages.map((s) => s.stageId)} strategy={verticalListSortingStrategy}>
                <List sx={{ py: 0 }}>
                  {activeStages.map((stage) => (
                    <SortableStageItem
                      key={stage.stageId}
                      stageId={stage.stageId}
                      label={stage.label}
                      color={stage.color}
                      teamRole={(stage as unknown as { teamRole?: string | null }).teamRole}
                      isSystem={stage.isSystem}
                      onEdit={handleOpenStageDialog}
                      onDelete={handleOpenDeleteDialog}
                    />
                  ))}
                </List>
              </SortableContext>
            </DndContext>
          )}
        </CardContent>
      </Card>

      {/* Stage Gates Configuration (only for client workflow) */}
      {pipelineType === 'client' && (
        <Card>
        <CardContent sx={{ p: 0 }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ borderBottom: 1, borderColor: 'divider' }}
          >
            {activeStages.map((stage, index) => {
              const gate = stageGates.find((g) => g.stageId === stage.stageId)
              const taskCount = gate?.tasks?.length || 0
              return (
                <Tab
                  key={stage.stageId}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {stage.label}
                      {taskCount > 0 && (
                        <Chip
                          label={taskCount}
                          size="small"
                          sx={{
                            height: 20,
                            minWidth: 20,
                            fontSize: '0.7rem',
                            backgroundColor: 'grey.200',
                          }}
                        />
                      )}
                    </Box>
                  }
                  value={index}
                />
              )
            })}
          </Tabs>

          <Box sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                {activeStages[activeTab]?.label || FALLBACK_STAGE_LABELS[currentStageId]} Tasks
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <IconButton onClick={loadStageGates} disabled={loading} size="small">
                  <RefreshIcon />
                </IconButton>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => handleOpenTaskDialog()}
                  disabled={loading || saving}
                  size="small"
                >
                  Add Task
                </Button>
                <FormControlLabel
                  control={
                    <Switch
                      checked={currentStageGate.allowOverride}
                      onChange={handleToggleOverride}
                      disabled={saving}
                    />
                  }
                  label="Allow Override"
                />
              </Box>
            </Box>

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={24} sx={{ color: '#C41E3D' }} />
              </Box>
            ) : currentStageGate.tasks.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography color="text.secondary">
                  No tasks configured for this stage. Add tasks that must be completed before advancing.
                </Typography>
              </Box>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Task</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell align="center">Required</TableCell>
                      <TableCell>Owner</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {currentStageGate.tasks
                      .sort((a, b) => a.sortOrder - b.sortOrder)
                      .map((task) => (
                        <TableRow key={task.id} hover>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {task.title}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {task.description || '-'}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            {task.required ? (
                              <Chip
                                icon={<RequiredIcon sx={{ fontSize: 16 }} />}
                                label="Required"
                                size="small"
                                color="error"
                              />
                            ) : (
                              <Chip label="Optional" size="small" color="default" />
                            )}
                          </TableCell>
                          <TableCell>
                            {task.ownerRole ? (
                              <Chip
                                label={ROLE_LABELS[task.ownerRole]}
                                size="small"
                                color="primary"
                                variant="outlined"
                              />
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                Stage Default
                                {STAGE_DEFAULT_ROLE[currentStageId]
                                  ? ` (${ROLE_LABELS[STAGE_DEFAULT_ROLE[currentStageId]!]})`
                                  : ''}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="right">
                            <IconButton size="small" onClick={(e) => handleOpenMenu(e, task)}>
                              <MoreVertIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        </CardContent>
      </Card>
      )}

      {/* Task Dialog */}
      <Dialog open={taskDialogOpen} onClose={handleCloseTaskDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingTask ? 'Edit Task' : 'Add Task'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Task Title"
              value={taskForm.title}
              onChange={(e) => setTaskForm({ ...taskForm, title: e.target.value })}
              fullWidth
              required
              placeholder="e.g., Schedule onboarding call"
            />
            <TextField
              label="Description"
              value={taskForm.description}
              onChange={(e) => setTaskForm({ ...taskForm, description: e.target.value })}
              fullWidth
              multiline
              rows={2}
              placeholder="Optional description of what needs to be done"
            />
            <FormControl fullWidth>
              <InputLabel>Owner Role</InputLabel>
              <Select
                value={taskForm.ownerRole}
                onChange={(e) => setTaskForm({ ...taskForm, ownerRole: e.target.value as TeamRole | '' })}
                label="Owner Role"
              >
                <MenuItem value="">
                  <em>Stage Default{STAGE_DEFAULT_ROLE[currentStageId] ? ` (${ROLE_LABELS[STAGE_DEFAULT_ROLE[currentStageId]!]})` : ''}</em>
                </MenuItem>
                {ROLE_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControlLabel
              control={
                <Switch
                  checked={taskForm.required}
                  onChange={(e) => setTaskForm({ ...taskForm, required: e.target.checked })}
                />
              }
              label="Required task (must be completed to advance stage)"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseTaskDialog}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveTask} disabled={saving || !taskForm.title.trim()}>
            {saving ? <CircularProgress size={20} color="inherit" /> : editingTask ? 'Save' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Actions Menu */}
      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={handleCloseMenu}>
        <MenuItem onClick={() => handleOpenTaskDialog(menuTask!)}>
          <EditIcon sx={{ mr: 1, fontSize: 18 }} />
          Edit
        </MenuItem>
        <MenuItem onClick={handleDeleteTask} sx={{ color: 'error.main' }}>
          <DeleteIcon sx={{ mr: 1, fontSize: 18 }} />
          Delete
        </MenuItem>
      </Menu>

      {/* Stage Dialog (Add/Edit) */}
      <Dialog open={stageDialogOpen} onClose={handleCloseStageDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingStage ? 'Edit Stage' : 'Add Stage'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {editingStage && (
              <TextField
                label="Stage ID"
                value={editingStage}
                fullWidth
                disabled
                helperText="Stage ID cannot be changed after creation"
              />
            )}
            <TextField
              label="Stage Label"
              value={stageForm.label}
              onChange={(e) => setStageForm({ ...stageForm, label: e.target.value })}
              fullWidth
              required
              placeholder="e.g., Final Review"
            />
            <TextField
              label="Color"
              type="color"
              value={stageForm.color}
              onChange={(e) => setStageForm({ ...stageForm, color: e.target.value })}
              fullWidth
              helperText="Hex color for the stage (e.g., #66bb6a)"
            />
            {pipelineType !== 'jobApplication' && (
              <FormControl fullWidth>
                <InputLabel>Default Team Role</InputLabel>
                <Select
                  value={stageForm.teamRole}
                  onChange={(e) => setStageForm({ ...stageForm, teamRole: e.target.value as TeamRole | '' })}
                  label="Default Team Role"
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {ROLE_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            {pipelineType === 'client' && (
              <FormControl fullWidth>
                {/* shrink + displayEmpty: "None" is a real choice and the default, so the
                    field must read "None" rather than sit blank under a raised label. */}
                <InputLabel shrink>Email when a client enters this stage</InputLabel>
                <Select
                  value={stageForm.entryEmail}
                  onChange={(e) => setStageForm({ ...stageForm, entryEmail: e.target.value })}
                  label="Email when a client enters this stage"
                  displayEmpty
                  notched
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {STAGE_ENTRY_EMAIL_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>
                  Sent once per client the first time they enter this stage, by any route. Skipped stages send nothing.
                </FormHelperText>
              </FormControl>
            )}
            {pipelineType === 'sales' && (
              <FormControlLabel
                control={
                  <Switch
                    checked={stageForm.showValue}
                    onChange={(e) => setStageForm({ ...stageForm, showValue: e.target.checked })}
                  />
                }
                label="Show Salary Total"
                sx={{ mt: 1 }}
              />
            )}
            {editingStage && pipelineType === 'client' && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                  Stage visibility
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Untick an item to hide it from both the staff client record and the
                  client portal while a client sits in this stage — one setting, so the
                  two cannot drift. Everything is shown by default.
                </Typography>
                <FormGroup>
                  {GATEABLE_STAGE_VISIBILITY_ENTRIES.map((entry) => {
                    const visible = !stageForm.hiddenPanels.includes(entry.id)
                    // `/portal/profile` is the portal's only tabbed page.
                    const portalWhere = entry.portalTab
                      ? `My Profile › ${entry.portalLabel ?? entry.label} tab`
                      : entry.portalLabel ?? entry.label
                    const appliesTo = [
                      entry.section ? `Record: ${RECORD_SECTION_LABELS[entry.section]}` : null,
                      entry.portalPath ? `Portal: ${portalWhere}` : null,
                    ]
                      .filter(Boolean)
                      .join(' · ')
                    return (
                      <FormControlLabel
                        key={entry.id}
                        sx={{ alignItems: 'flex-start', mb: 0.5 }}
                        control={
                          <Checkbox
                            size="small"
                            checked={visible}
                            onChange={(e) => {
                              const next = e.target.checked
                                ? stageForm.hiddenPanels.filter((id) => id !== entry.id)
                                : [...stageForm.hiddenPanels, entry.id]
                              setStageForm({ ...stageForm, hiddenPanels: next })
                            }}
                          />
                        }
                        label={
                          <Box component="span" sx={{ display: 'block', pt: 0.5 }}>
                            {entry.label}
                            <Typography
                              variant="caption"
                              component="span"
                              color="text.secondary"
                              sx={{ display: 'block' }}
                            >
                              {appliesTo}
                            </Typography>
                          </Box>
                        }
                      />
                    )
                  })}
                </FormGroup>
              </Box>
            )}
            {editingStage && (
              <FormControlLabel
                control={
                  <Switch
                    checked={stageForm.isProtected}
                    onChange={(e) => setStageForm({ ...stageForm, isProtected: e.target.checked })}
                  />
                }
                label="Protected (block delete)"
                sx={{ mt: 1 }}
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseStageDialog}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveStage} disabled={saving || !stageForm.label.trim()}>
            {saving ? <CircularProgress size={20} color="inherit" /> : editingStage ? 'Save' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Stage Confirmation */}
      <Dialog open={deleteStageDialogOpen} onClose={handleCloseDeleteDialog} maxWidth="sm">
        <DialogTitle>Delete Stage?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will permanently delete this stage.
          </DialogContentText>
          <Alert severity="warning" sx={{ mt: 2 }}>
            This action cannot be undone. The stage will be removed from the workflow.
          </Alert>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseDeleteDialog}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmDelete}
            disabled={saving}
          >
            {saving ? <CircularProgress size={20} color="inherit" /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
